// # Payment Analytics Component
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../../auth/state';
import DashboardHeader from '../../../../../components/DashboardHeader';
import DashboardFooter from '../../../../../components/DashboardFooter';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { 
  FaChartLine, 
  FaChartPie, 
  FaChartBar, 
  FaGlobe,
  FaUsers,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaClock,
  FaChalkboardTeacher,
  FaFilter,
  FaTimes,
  FaDollarSign,
  FaGlobeAmericas,
  FaShoppingCart,
  FaUserTie,
  FaGraduationCap
} from 'react-icons/fa';
import './pay_analytics.css';

// # Import the specialized EGP Revenue Over Time component
import EgpRevenueOverTime from './pay_lytics/egp_rev_ovtime';

// # Import the Mentor Performance component and add MentorOverview
import MentorPerformance from './pay_lytics/mentor_perform';
import MentorOverview from './pay_lytics/mentor_overview';
import TutorOverview from './pay_lytics/tutor_overview';

// # Import the Customer Analysis component
import CustomerAnalysis from './pay_lytics/customer_analysis';

// # Import the Product Analysis component
import ProductAnalysis from './pay_lytics/product_analysis';

// # Import team members configuration
import { teamMembers } from './register_pay_calc';

// # Memoize the MentorPerformance component to prevent unnecessary re-renders
const MemoizedMentorPerformance = React.memo(MentorPerformance);

const PaymentAnalytics = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [activeTab, setActiveTab] = useState('revenue');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({
    startDate: new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [dateRange, setDateRange] = useState(tempDateRange);
  const [analyticsData, setAnalyticsData] = useState({
    revenueOverTime: [],
    revenueByProduct: [],
    revenueByNationality: [],
    productDistribution: [],
    monthlyRevenue: [],
    currencyDistribution: [],
    mentorPerformance: [],
    customerStats: {
      totalCustomers: 0,
      newCustomersThisMonth: 0,
      averageTransactionValue: 0,
      repeatCustomers: 0
    }
  });

  // # Color palette for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  // # Add state for selected mentor
  const [selectedMentor, setSelectedMentor] = useState('elhadidy'); // Default to first mentor
  
  // # Update the tabs to include an overview option
  const [mentorTab, setMentorTab] = useState('overview'); // Default to overview

  // # Add state for tutor list
  const [tutorList, setTutorList] = useState([]);

  // # Fetch tutors from database
  useEffect(() => {
    const fetchTutors = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersData = usersSnapshot.docs.map(doc => ({
          uid: doc.id,
          ...doc.data()
        }));

        // # Filter users to get tutors
        const tutorsData = usersData.filter(user => user.role === 'tutor').map(tutor => ({
          id: tutor.uid,
          name: `${tutor.firstName} ${tutor.lastName}`.trim(),
          email: tutor.email
        }));

        setTutorList(tutorsData);
      } catch (error) {
        console.error('Error fetching tutors:', error);
      }
    };

    if (isAdmin) {
      fetchTutors();
    }
  }, [isAdmin]);

  // # Fetch and process analytics data
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setLoading(true);
      try {
        const paymentsRef = collection(db, 'financial');
        const paymentsSnap = await getDocs(paymentsRef);
        
        let allPayments = [];
        paymentsSnap.docs.forEach(doc => {
          // # Document ID now contains year_month format (e.g., "2024_03")
          const docId = doc.id;
          const yearMonthData = doc.data();
          
          // # Check if payments map exists in the document
          if (yearMonthData.payments) {
            // # Extract payments from the payments map
            Object.entries(yearMonthData.payments).forEach(([paymentId, paymentData]) => {
              // # Only process payment transactions
              if (paymentId.startsWith('payment_') && paymentData.transaction_type === 'payment') {
                // # Extract data from new structure - directly use stored values without recalculation
                allPayments.push({
                  payment_id: paymentId,
                  doc_id: docId, // # Store the document ID for reference
                  timestamp: paymentData.metadata?.created_at || '',
                  egp_total: parseFloat(paymentData.pay?.egp_total) || 0,
                  egp_net: parseFloat(paymentData.pay?.egp_net) || 0,
                  product: paymentData.pay?.product || 'unknown',
                  duration: paymentData.pay?.duration || '',
                  nationality: paymentData.user?.nationality || 'Unknown',
                  email: paymentData.user?.email || '',
                  name: paymentData.user?.name || '',
                  currency: paymentData.pay?.currency || '',
                  currency_symbol: paymentData.pay?.currency_symbol || '',
                  mentor: paymentData.mentor?.mentor_name || '',
                  mentor_id: paymentData.mentor?.uid || '',
                  tutor: paymentData.tutor?.tutor_name || '',
                  tutor_id: paymentData.tutor?.uid || '',
                  budget_dvd_amount: paymentData.budget_dvd?.egp_amount || 0,
                  budget_dvd_percent: paymentData.budget_dvd?.percentage_of_total || 0,
                  // # Directly use the shares data without recalculation
                  shares: paymentData.shares || {}
                });
              }
            });
          }
        });

        // # Filter payments by date range
        const filteredPayments = allPayments.filter(payment => {
          if (!payment.timestamp) return false;
          
          const paymentDate = new Date(payment.timestamp);
          const startDate = new Date(dateRange.startDate);
          const endDate = new Date(dateRange.endDate);
          
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);
          
          return paymentDate >= startDate && paymentDate <= endDate;
        });
        
        // # Process analytics data with the filtered payments
        processAnalyticsData(filteredPayments);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
        setError('Failed to load analytics data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [dateRange]);

  // # Process analytics data for charts
  const processAnalyticsData = (payments) => {
    // # Revenue Over Time
    const revenueByMonth = {};
    payments.forEach(payment => {
      const date = new Date(payment.timestamp);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      // # Use the pre-calculated egp_total directly
      revenueByMonth[monthKey] = (revenueByMonth[monthKey] || 0) + Number(payment.egp_total);
    });

    // # Revenue by Product
    const revenueByProduct = {};
    payments.forEach(payment => {
      revenueByProduct[payment.product] = (revenueByProduct[payment.product] || 0) + Number(payment.egp_total);
    });

    // # Revenue by Nationality
    const revenueByNationality = {};
    payments.forEach(payment => {
      revenueByNationality[payment.nationality] = (revenueByNationality[payment.nationality] || 0) + Number(payment.egp_total);
    });

    // # Product Distribution
    const productCount = {};
    payments.forEach(payment => {
      productCount[payment.product] = (productCount[payment.product] || 0) + 1;
    });

    // # Currency Distribution
    const currencyCount = {};
    payments.forEach(payment => {
      currencyCount[payment.currency] = (currencyCount[payment.currency] || 0) + 1;
    });

    // # Mentor Performance Metrics
    const mentorStats = {};
    payments.forEach(payment => {
      if (payment.mentor && payment.product.includes('mentorship')) {
        if (!mentorStats[payment.mentor]) {
          mentorStats[payment.mentor] = {
            totalRevenue: 0,
            studentCount: new Set(),
            paymentCount: 0,
            averagePayment: 0
          };
        }
        mentorStats[payment.mentor].totalRevenue += Number(payment.egp_total);
        mentorStats[payment.mentor].studentCount.add(payment.email);
        mentorStats[payment.mentor].paymentCount += 1;
      }
    });

    // # Calculate average payment per mentor
    Object.keys(mentorStats).forEach(mentor => {
      mentorStats[mentor].averagePayment = 
        mentorStats[mentor].totalRevenue / mentorStats[mentor].paymentCount;
      mentorStats[mentor].studentCount = mentorStats[mentor].studentCount.size;
    });

    // # Customer Stats
    const uniqueCustomers = new Set(payments.map(p => p.email));
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const newCustomersThisMonth = new Set(
      payments
        .filter(p => {
          const paymentDate = new Date(p.timestamp);
          return paymentDate.getMonth() === currentMonth && 
                 paymentDate.getFullYear() === currentYear;
        })
        .map(p => p.email)
    );

    // # Format data for charts
    const formattedData = {
      revenueOverTime: Object.entries(revenueByMonth).map(([month, amount]) => ({
        month,
        amount
      })),
      revenueByProduct: Object.entries(revenueByProduct).map(([product, amount]) => ({
        product,
        amount
      })),
      revenueByNationality: Object.entries(revenueByNationality).map(([nationality, amount]) => ({
        nationality,
        amount
      })),
      productDistribution: Object.entries(productCount).map(([product, count]) => ({
        product,
        count
      })),
      currencyDistribution: Object.entries(currencyCount).map(([currency, count]) => ({
        currency,
        count
      })),
      mentorPerformance: Object.entries(mentorStats).map(([mentor, stats]) => ({
        mentor,
        totalRevenue: stats.totalRevenue,
        studentCount: stats.studentCount,
        averagePayment: stats.averagePayment
      })),
      customerStats: {
        totalCustomers: uniqueCustomers.size,
        newCustomersThisMonth: newCustomersThisMonth.size,
        averageTransactionValue: payments.reduce((acc, curr) => acc + Number(curr.egp_total), 0) / payments.length,
        repeatCustomers: payments.length - uniqueCustomers.size
      }
    };

    setAnalyticsData(formattedData);
  };

  // # Handle date range change in dialog
  const handleTempDateChange = (e) => {
    const { name, value } = e.target;
    setTempDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // # Apply date filter
  const handleApplyFilter = () => {
    setDateRange(tempDateRange);
    setIsFilterDialogOpen(false);
  };

  // # Cancel date filter
  const handleCancelFilter = () => {
    setTempDateRange(dateRange);
    setIsFilterDialogOpen(false);
  };

  // # Memoize the mentor data callback to prevent re-renders
  const handleMentorDataProcessed = useCallback((data) => {
    // Optional: You can use this callback to update other components
    // Only log once to avoid console spam
    if (data && data.length > 0) {
      console.log('Mentor data processed successfully');
    }
  }, []);

  // # Redirect if not admin
  React.useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  return (
    <div className="pay_analytics_page">
      <DashboardHeader />
      
      <main className="pay_analytics_content">
        <div className="pay_analytics_title-container">
          <h1>Payment Analytics</h1>
          <button 
            className="pay_analytics_filter-button"
            onClick={() => setIsFilterDialogOpen(true)}
          >
            <FaFilter /> Filter by Date
          </button>
        </div>

        {/* Filter Dialog */}
        {isFilterDialogOpen && (
          <div className="pay_analytics_dialog-overlay">
            <div className="pay_analytics_dialog">
              <div className="pay_analytics_dialog-header">
                <h2 className="pay_analytics_dialog-title">Filter by Date Range</h2>
                <button 
                  className="pay_analytics_dialog-close"
                  onClick={handleCancelFilter}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="pay_analytics_date-filter">
                <div className="pay_analytics_date-inputs">
                  <div className="pay_analytics_date-field">
                    <label>Start Date:</label>
                    <input
                      type="date"
                      name="startDate"
                      value={tempDateRange.startDate}
                      onChange={handleTempDateChange}
                      max={tempDateRange.endDate}
                    />
                  </div>
                  <div className="pay_analytics_date-field">
                    <label>End Date:</label>
                    <input
                      type="date"
                      name="endDate"
                      value={tempDateRange.endDate}
                      onChange={handleTempDateChange}
                      min={tempDateRange.startDate}
                    />
                  </div>
                </div>
                <div className="pay_analytics_dialog-actions">
                  <button 
                    className="pay_analytics_dialog-button cancel"
                    onClick={handleCancelFilter}
                  >
                    Cancel
                  </button>
                  <button 
                    className="pay_analytics_dialog-button apply"
                    onClick={handleApplyFilter}
                  >
                    Apply Filter
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tabs */}
        <div className="pay_analytics_tabs">
          <button 
            className={`pay_analytics_tab ${activeTab === 'revenue' ? 'active' : ''}`}
            onClick={() => setActiveTab('revenue')}
          >
            <FaChartLine /> Revenue Analysis
          </button>
          <button 
            className={`pay_analytics_tab ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            <FaChartPie /> Product Analysis
          </button>
          <button 
            className={`pay_analytics_tab ${activeTab === 'customers' ? 'active' : ''}`}
            onClick={() => setActiveTab('customers')}
          >
            <FaUsers /> Customer Analysis
          </button>
          <button 
            className={`pay_analytics_tab ${activeTab === 'mentors' ? 'active' : ''}`}
            onClick={() => setActiveTab('mentors')}
          >
            <FaChalkboardTeacher /> Mentor Performance
          </button>
          <button 
            className={`pay_analytics_tab ${activeTab === 'tutors' ? 'active' : ''}`}
            onClick={() => setActiveTab('tutors')}
          >
            <FaGraduationCap /> Tutor Performance **
          </button>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="pay_analytics_loading">Loading analytics data...</div>
        ) : (
          <div className="pay_analytics_dashboard">
            {/* Revenue Analysis Tab */}
            {activeTab === 'revenue' && (
              <div className="pay_analytics_section">
                {/* Revenue Stats Cards */}
                <div className="pay_analytics_stats-grid">
                  <div className="pay_analytics_stat-card">
                    <FaMoneyBillWave className="pay_analytics_stat-icon" />
                    <h3>Total EGP Revenue</h3>
                    <p className="pay_analytics_stat-value">
                      E£ {analyticsData.revenueOverTime.reduce((acc, curr) => acc + curr.amount, 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="pay_analytics_stat-card">
                    <FaCalendarAlt className="pay_analytics_stat-icon" />
                    <h3>Average Monthly EGP Revenue</h3>
                    <p className="pay_analytics_stat-value">
                      E£ {(analyticsData.revenueOverTime.reduce((acc, curr) => acc + curr.amount, 0) / 
                          Math.max(1, analyticsData.revenueOverTime.length)).toLocaleString()}
                    </p>
                  </div>
                  <div className="pay_analytics_stat-card">
                    <FaClock className="pay_analytics_stat-icon" />
                    <h3>Average Transaction Value in EGP</h3>
                    <p className="pay_analytics_stat-value">
                      E£ {analyticsData.customerStats.averageTransactionValue.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Replace the existing Revenue Over Time Chart with the specialized component */}
                <div className="pay_analytics_chart-container">
                  <h3>EGP Revenue Over Time</h3>
                  <EgpRevenueOverTime 
                    dateRange={dateRange}
                    onDataProcessed={(data) => {
                      // # Optional: You can use this callback to update other components
                      // # or store the processed data for other charts
                    }}
                  />
                </div>

                {/* Revenue by Product Chart - Removed as it's redundant with the chart in EgpRevenueOverTime */}

              </div>
            )}

            {/* Product Analysis Tab */}
            {activeTab === 'products' && (
              <div className="pay_analytics_section">
                <ProductAnalysis 
                  dateRange={dateRange}
                  onDataProcessed={(data) => {
                    // Optional: You can use this callback to update other components
                    console.log('Product analysis data processed:', data);
                  }}
                />
              </div>
            )}

            {/* Customer Analysis Tab */}
            {activeTab === 'customers' && (
              <div className="pay_analytics_section">
                <CustomerAnalysis 
                  dateRange={dateRange}
                  onDataProcessed={(data) => {
                    // Optional: You can use this callback to update other components
                    console.log('Customer analysis data processed:', data);
                  }}
                />
              </div>
            )}

            {/* Mentor Performance Tab */}
            {activeTab === 'mentors' && (
              <div className="pay_analytics_section">
                {/* Mentor Tab Navigation */}
                <div className="pay_analytics_mentor-tabs">
                  <button 
                    className={`pay_analytics_mentor-tab ${mentorTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setMentorTab('overview')}
                  >
                    Mentor Overview
                  </button>
                  <button 
                    className={`pay_analytics_mentor-tab ${mentorTab === 'individual' ? 'active' : ''}`}
                    onClick={() => setMentorTab('individual')}
                  >
                    Individual Analysis
                  </button>
                </div>

                {/* Mentor Overview Tab */}
                {mentorTab === 'overview' && (
                  <MentorOverview 
                    dateRange={dateRange}
                    teamMembers={teamMembers}
                  />
                )}
                
                {/* Individual Mentor Analysis Tab */}
                {mentorTab === 'individual' && (
                  <>
                    {/* Mentor Selector */}
                    <div className="pay_analytics_mentor-selector">
                      <h3>Select Mentor</h3>
                      <div className="pay_analytics_mentor-buttons">
                        {teamMembers.map(mentor => (
                          <button
                            key={mentor.id}
                            className={`pay_analytics_mentor-button ${selectedMentor === mentor.id ? 'active' : ''}`}
                            onClick={() => setSelectedMentor(mentor.id)}
                          >
                            {mentor.name}
                          </button>
                        ))}
                      </div>
                </div>
                    
                    {/* Mentor Performance Component */}
                    <MemoizedMentorPerformance 
                      mentorId={selectedMentor}
                      dateRange={dateRange}
                      onDataProcessed={handleMentorDataProcessed}
                    />
                  </>
                )}
              </div>
            )}

            {/* Tutor Performance Tab */}
            {activeTab === 'tutors' && (
              <div className="pay_analytics_section">
                <TutorOverview 
                  dateRange={dateRange}
                  tutorList={tutorList}
                />
              </div>
            )}
          </div>
        )}
      </main>

      <DashboardFooter />
    </div>
  );
};

export default PaymentAnalytics;
